import React, { useEffect, useState } from 'react';
import { ProtectedLayout } from '../components/layout/ProtectedLayout';
import { fetchActivityLogs, ActivityLog } from '../api/logs';
import { AuthenticatedUser } from '../components/layout/NavigationDrawer';

const AdminLogs: React.FC = () => (
  <ProtectedLayout>
    {({ user }) => <AdminLogsContent user={user} />}
  </ProtectedLayout>
);

interface AdminLogsContentProps {
  user: AuthenticatedUser;
}

const toMapEmbedUrl = (latitude: number, longitude: number): string => {
  const delta = 0.01;
  const left = (longitude - delta).toFixed(6);
  const bottom = (latitude - delta).toFixed(6);
  const right = (longitude + delta).toFixed(6);
  const top = (latitude + delta).toFixed(6);
  return `https://www.openstreetmap.org/export/embed.html?bbox=${left}%2C${bottom}%2C${right}%2C${top}&layer=mapnik&marker=${latitude.toFixed(6)}%2C${longitude.toFixed(6)}`;
};

const AdminLogsContent: React.FC<AdminLogsContentProps> = ({ user }) => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user.is_staff) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem('access_token');
    if (!token) {
      setError('Missing access token.');
      setLoading(false);
      return;
    }
    fetchActivityLogs(token)
      .then(data => {
        setLogs(data);
        setError('');
      })
      .catch(() => setError('Failed to load activity logs'))
      .finally(() => setLoading(false));
  }, [user.is_staff]);

  if (!user.is_staff) {
    return (
      <div className="space-y-6">
        <header className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <h1 className="text-3xl font-semibold text-foreground">Activity Logs</h1>
          <p className="mt-2 text-sm text-muted-foreground">You do not have permission to view this page.</p>
        </header>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <h1 className="text-3xl font-semibold text-foreground">Kitten Activity Logs</h1>
        <p className="mt-2 text-sm text-muted-foreground">Track significant updates and actions.</p>
      </header>
      {loading && (
        <div className="space-y-2 rounded-xl border border-border bg-card p-6 shadow-sm">
          <div className="h-4 w-40 animate-pulse rounded bg-muted" />
          <div className="h-3 w-full animate-pulse rounded bg-muted" />
          <div className="h-3 w-full animate-pulse rounded bg-muted" />
          <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
        </div>
      )}
      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}
      {!loading && !error && (
        <ul className="space-y-3">
          {logs.map(log => (
            <li
              key={log.id}
              className="rounded-lg border border-border bg-background px-4 py-3 shadow-sm"
            >
              <span className="text-sm font-semibold text-foreground">
                {new Date(log.timestamp).toLocaleString()}
              </span>
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{log.user}</span>: {log.action}
              </p>
              {log.latitude !== null && log.longitude !== null && (
                <div className="mt-3 overflow-hidden rounded-md border border-border">
                  <iframe
                    title={`Location for log ${log.id}`}
                    src={toMapEmbedUrl(log.latitude, log.longitude)}
                    className="h-36 w-full"
                    loading="lazy"
                  />
                  <div className="border-t border-border bg-muted/40 px-2 py-1 text-xs text-muted-foreground">
                    {log.latitude.toFixed(5)}, {log.longitude.toFixed(5)}
                    {log.accuracy_meters !== null ? ` (accuracy ${Math.round(log.accuracy_meters)}m)` : ''}
                  </div>
                </div>
              )}
            </li>
          ))}
          {logs.length === 0 && (
            <li className="rounded-lg border border-dashed border-border/80 bg-muted/40 px-4 py-8 text-center text-sm text-muted-foreground">
              No logs recorded yet.
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default AdminLogs;
